import { COMMON_TEXT } from '@/helpers/common-text';
import {
  DEVICE_TYPE,
  MEMORY_STORE,
  QUERY_STRING_STORE,
} from '@/helpers/constants';
import { addDays } from 'date-fns';
import { FilterMatchMode } from 'primereact/api';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import DeviceStatusOverviewHistory from './DeviceStatusOverviewHistory';
import DeviceStatusOverviewNow from './DeviceStatusOverviewNow';
import {
  fetchData,
  fetchDataHistory,
  fetchDataHistoryEmptyEndDate,
} from './query-request';
import fileService from '@/services/file-service';
import { Message } from 'primereact/message';
import { cloneDeep } from 'lodash';
import { format } from 'date-fns';


@connect(state => ({
  querystring: state.querystring,
  sessionStore: state.session,
}))
class DeviceStatusOverview extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data: [],
      dataTable: [],
      dataTableHistory: [],
      floor:
        this.props?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_FLOOR]?.[0],
      isLoading: false,
      isLoadingHistory: false,
      floorMapUrl: '',
      floorMapError: false,
      selectedDeviceModelList: [],
      deviceModelOptions: [],
      tabActiveIndex: 0,
      startDate: new Date(),
      endDate: new Date(),
      searchQuery: '',
      selectedFilters: {
        product: true,
        device_type: true,
        device_name: true,
      },
    };

    this.dataTableRef = React.createRef();
    this.dataHistoryTableRef = React.createRef();
    this.filters = {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      product: { value: null, matchMode: FilterMatchMode.CONTAINS },
      device_type: { value: null, matchMode: FilterMatchMode.CONTAINS },
      device_name: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      },
    };
  }

  validateFloorMapUrl(url, successCallback, errorCallback) {
    var img = new Image();
    img.onload = function () {
      successCallback(url);
    };
    img.onerror = function () {
      errorCallback(url);
    };
    img.src = url;
  }

  getFloorMapUrl = floorId => {
    const floors = this.props?.sessionStore[MEMORY_STORE.FLOORS] || [];
    const floor = floors.find(floor => floor.floor_id === floorId);
    this.validateFloorMapUrl(
      floor?.floor_map_url,
      () => {
        this.setState({ floorMapError: false });
      },
      () => {
        this.setState({ floorMapError: true });
      }
    );
    return floor ? floor.floor_map_url : '';
  };

  componentDidMount() {
    const { floor } = this.state;
    let startDate = addDays(new Date(), -7);
    startDate.setHours(0, 0, 0, 0);
    let endDate = addDays(new Date(), -1);
    endDate.setHours(23, 59, 59, 999);
    this.setState(
      {
        startDate: startDate,
        endDate: endDate,
      },
      () => {
        if (floor) {
          this.setState({ floorMapUrl: this.getFloorMapUrl(floor) });
          this.fetchDataByFloorId();
          this.fetchDataHistoryByFloorId();
        }
      }
    );
  }

  componentDidUpdate(prevProps) {
    const floor =
      this.props?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_FLOOR]?.[0];
    if (
      floor !==
      prevProps?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_FLOOR]?.[0]
    ) {
      this.setState({ floor, floorMapUrl: this.getFloorMapUrl(floor) }, () => {
        this.fetchDataByFloorId();
        this.fetchDataHistoryByFloorId();
      });
    }
  }

  getDeviceOptions = ({ dataList }) => {
    const deviceTypeSet = new Set(dataList.map(item => item.device_type));

    const deviceOptions = [];
    for (const key in DEVICE_TYPE) {
      if (DEVICE_TYPE.hasOwnProperty(key) && deviceTypeSet.has(key)) {
        deviceOptions.push({ code: key, name: DEVICE_TYPE[key] });
      }
    }
    return deviceOptions;
  };

  fetchDataByFloorId = async () => {
    const { floor } = this.state;
    if (!floor) return;
    this.setState({ isLoading: true });
    let dataFloorMap = await fetchData({ floorId: floor });
    dataFloorMap = dataFloorMap ?? [];

    const deviceOptions = this.getDeviceOptions({ dataList: dataFloorMap });

    const statusIconList = dataFloorMap
      .filter(item => item.icon && item.icon !== 'default')
      .map(item => item.icon);
    
    const statusIconBase64Map = {};
    
    try {
      if (statusIconList.length > 0) {
        const iconUrls = await fileService.getFileUrls(statusIconList);
        
        statusIconList.forEach(icon => {
          statusIconBase64Map[icon] = iconUrls[icon] || 'default';
        });
      }
    } catch (error) {
      console.error('Failed to load status icons:', error);
      for (const statusIcon of statusIconList) {
        try {
          const statusIconBase64 = await fileService.getFileUrl(statusIcon);
          statusIconBase64Map[statusIcon] = statusIconBase64;
        } catch (iconError) {
          console.error(`Failed to load icon ${statusIcon}:`, iconError);
          statusIconBase64Map[statusIcon] = 'default';
        }
      }
    }

    const updatedFloorMapData = dataFloorMap.map(item => ({
      position: {
        x: parseFloat(item?.position_x),
        y: parseFloat(item?.position_y),
      },
      data: item ?? null,
      iconImage: statusIconBase64Map[item.icon] ?? 'default',
    }));

    // Also add iconImage to dataTable for use in the table columns
    const updatedDataTable = dataFloorMap.map(item => ({
      ...item,
      iconImage: statusIconBase64Map[item.icon] ?? 'default',
    }));

    this.setState({
      data: updatedFloorMapData,
      dataTable: cloneDeep(updatedDataTable),
      deviceModelOptions: deviceOptions,
      selectedDeviceModelList: deviceOptions,
      isLoading: false,
    });
  };

  fetchDataHistoryByFloorId = async () => {
    const { floor, startDate, endDate } = this.state;
    if (!floor) return;
    
    this.setState({ isLoadingHistory: true });
    
    let dataHistoryRes = await fetchDataHistory({
      floorId: floor,
      fromDate: format(startDate, 'yyyy-MM-dd 00:00:00'),
      toDate: format(endDate, 'yyyy-MM-dd 23:59:59'),
    });

    dataHistoryRes = dataHistoryRes ?? [];

    let dataHistoryEmptyEndDateRes = await fetchDataHistoryEmptyEndDate({
      floorId: floor,
      fromDate: format(startDate, 'yyyy-MM-dd 00:00:00'),
      toDate: format(endDate, 'yyyy-MM-dd 23:59:59'),
    });

    dataHistoryEmptyEndDateRes = dataHistoryEmptyEndDateRes ?? [];
    dataHistoryRes = dataHistoryRes.concat(dataHistoryEmptyEndDateRes);


    // Load icon images for history data as well
    const statusIconList = dataHistoryRes
      .filter(item => item.icon && item.icon !== 'default')
      .map(item => item.icon);
    
    const statusIconBase64Map = {};
    
    try {
      if (statusIconList.length > 0) {
        const iconUrls = await fileService.getFileUrls(statusIconList);
        
        statusIconList.forEach(icon => {
          statusIconBase64Map[icon] = iconUrls[icon] || 'default';
        });
      }
    } catch (error) {
      console.error('Failed to load status icons:', error);
      for (const statusIcon of statusIconList) {
        try {
          const statusIconBase64 = await fileService.getFileUrl(statusIcon);
          statusIconBase64Map[statusIcon] = statusIconBase64;
        } catch (iconError) {
          console.error(`Failed to load icon ${statusIcon}:`, iconError);
          statusIconBase64Map[statusIcon] = 'default';
        }
      }
    }

    // Add iconImage to history data table
    const updatedDataTableHistory = dataHistoryRes.map(item => ({
      ...item,
      iconImage: statusIconBase64Map[item.icon] ?? 'default',
    }));

    this.setState({
      dataTableHistory: cloneDeep(updatedDataTableHistory),
      isLoadingHistory: false,
    });
  };

  handleRefresh = () => {
    const { tabActiveIndex } = this.state;
    if (tabActiveIndex === 0) {
      this.fetchDataByFloorId();
    } else {
      this.fetchDataHistoryByFloorId();
    }
  };

  handleTabChange = (index) => {
    this.setState({ tabActiveIndex: index });
  };

  handleSearchChange = (e) => {
    this.setState({ searchQuery: e.target.value });
  };

  handleFilterChange = (filterName) => {
    this.setState(prevState => ({
      selectedFilters: {
        ...prevState.selectedFilters,
        [filterName]: !prevState.selectedFilters[filterName],
      },
    }));
  };

  handleDateChange = (startDate, endDate) => {
    if (startDate) {
      this.setState({ startDate });
    }

    if (endDate) {
      this.setState({ endDate });
    }
  };

  render() {
    const {
      tabActiveIndex,
      data,
      dataTable,
      dataTableHistory,
      isLoading,
      isLoadingHistory,
      startDate,
      endDate,
      floorMapUrl,
      floorMapError,
      selectedDeviceModelList,
      deviceModelOptions,
      searchQuery,
      selectedFilters,
      floor,
    } = this.state;

    return (
      <div className="device-status-overview">
        <div className="tab-container">
          <div className="tab-menu">
            <button
              className={`tab-btn ${tabActiveIndex === 0 ? 'active' : ''}`}
              onClick={() => this.handleTabChange(0)}
            >
              現在
            </button>
            <button
              className={`tab-btn ${tabActiveIndex === 1 ? 'active' : ''}`}
              onClick={() => this.handleTabChange(1)}
            >
              履歴
            </button>
          </div>
          <button className="refresh-button" onClick={this.handleRefresh}>
            <i className="pi pi-refresh" />
          </button>
        </div>

        <div className="device-status-content-wrapper">
          {!floor ? (
            <div className="flex align-items-center justify-content-center" style={{ height: '400px' }}>
              <Message
                severity="error"
                text={COMMON_TEXT.FLOOR_NOT_SELECTED_PLEASE_SELECT}
              />
            </div>
          ) : (
            <div className="device-status-content">
              {tabActiveIndex === 0 ? (
                <DeviceStatusOverviewNow
                  dataTable={dataTable}
                  data={data}
                  isLoading={isLoading}
                  floorMapUrl={floorMapUrl}
                  floorMapError={floorMapError}
                  selectedDeviceModelList={selectedDeviceModelList}
                  deviceModelOptions={deviceModelOptions}
                  searchQuery={searchQuery}
                  selectedFilters={selectedFilters}
                  onSearchChange={this.handleSearchChange}
                  onFilterChange={this.handleFilterChange}
                />
              ) : (
                <DeviceStatusOverviewHistory
                  dataTableHistory={dataTableHistory}
                  isLoadingHistory={isLoadingHistory}
                  endDate={endDate}
                  startDate={startDate}
                  floor={floor}
                  onDateChange={this.handleDateChange}
                />
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}

DeviceStatusOverview.propTypes = {};

export default DeviceStatusOverview;
